import React, { useCallback, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { ContentCopy, OpenInNew } from '@mui/icons-material';
import type { TableColumnDisplayAs } from '../Settings/reportDsl';

export type { TableColumnDisplayAs };

export const resolveTableColumnDisplayAs = (
  field: string | undefined,
  columnDisplay?: Record<string, TableColumnDisplayAs>,
): TableColumnDisplayAs | undefined => {
  if (!field) return undefined;
  const configured = columnDisplay?.[field];
  if (configured) return configured;
  if (field === 'page_url') return 'url_actions';
  if (field === 'custom_dimensions.share_paths' || field.endsWith('.share_paths')) {
    return 'comma_multiline';
  }
  return undefined;
};

export const TableUrlActionsCell: React.FC<{ url: string }> = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const trimmed = url.trim();
  if (!trimmed) return null;

  const canOpen = /^https?:\/\//i.test(trimmed);

  const handleOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canOpen) return;
    window.open(trimmed, '_blank', 'noopener,noreferrer');
  }, [canOpen, trimmed]);

  const handleCopy = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(trimmed);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard may be unavailable */
    }
  }, [trimmed]);

  return (
    <Stack direction="row" spacing={0.25} alignItems="center" onClick={(e) => e.stopPropagation()}>
      {canOpen && (
        <Tooltip title="Open link in new tab">
          <IconButton
            size="small"
            aria-label="Open shared link"
            onClick={handleOpen}
          >
            <OpenInNew fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
        <IconButton
          size="small"
          aria-label="Copy shared link"
          onClick={handleCopy}
          color={copied ? 'success' : 'default'}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

export const TableCommaMultilineCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined || value === '') return null;

  const parts = String(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {parts.map((part, index) => (
        <Typography
          key={`${index}-${part}`}
          variant="body2"
          component="div"
          sx={{ fontSize: 12, lineHeight: 1.45, wordBreak: 'break-all' }}
        >
          {part}
        </Typography>
      ))}
    </Box>
  );
};

/** Render a raw byte count as megabytes (binary MiB, labelled "MB"). */
export const formatBytesAsMegabytes = (raw: unknown): string => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return '';
  const mb = n / (1024 * 1024);
  const decimals = mb >= 100 ? 0 : mb >= 10 ? 1 : 2;
  return `${mb.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} MB`;
};

/** Render a raw millisecond duration as minutes (one decimal under 10). */
export const formatDurationAsMinutes = (raw: unknown): string => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return '';
  const minutes = n / 60000;
  const decimals = minutes >= 10 ? 1 : 2;
  return `${minutes.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })} min`;
};

/**
 * Integer count capped at 100 — anything ≥ 100 reads as "100+", keeping
 * "result_total" style columns compact while still flagging large result
 * sets. Non-numeric input renders as an empty string.
 */
export const formatCountCapped100 = (raw: unknown): string => {
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return '';
  if (n >= 100) return '100+';
  return Math.round(n).toLocaleString();
};

/** Normalize a boolean-ish value ('true'/'false'/1/0/'1'/'0') to a boolean. */
const toBooleanish = (raw: unknown): boolean | null => {
  if (raw === null || raw === undefined || raw === '') return null;
  if (typeof raw === 'boolean') return raw;
  const s = String(raw).trim().toLowerCase();
  if (s === 'true' || s === '1' || s === 'yes') return true;
  if (s === 'false' || s === '0' || s === 'no') return false;
  return null;
};

export const TableLinkClickHereCell: React.FC<{ url: string }> = ({ url }) => {
  const trimmed = url.trim();
  if (!trimmed || !/^https?:\/\//i.test(trimmed)) {
    return <Typography variant="body2" sx={{ fontSize: 12 }}>{trimmed || '—'}</Typography>;
  }
  return (
    <Typography
      component="a"
      href={trimmed}
      target="_blank"
      rel="noopener noreferrer"
      variant="body2"
      onClick={(e) => e.stopPropagation()}
      sx={(theme) => ({
        fontSize: 13,
        fontWeight: 600,
        color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.main,
        textDecoration: 'underline',
        cursor: 'pointer',
        '&:hover': {
          color: theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.info.dark,
        },
      })}
    >
      Click Here
    </Typography>
  );
};

/**
 * Render a JSON string (an object, or an array of filter objects) as a
 * compact, readable list. Each leaf renders as `key: value`. Empty,
 * null, or unparseable input renders as "None".
 */
export const TableFiltersJsonCell: React.FC<{ value: unknown }> = ({ value }) => {
  const noneNode = <Typography variant="body2" sx={{ fontSize: 12, color: 'text.secondary' }}>None</Typography>;
  if (value === null || value === undefined) return noneNode;
  const str = String(value).trim();
  if (!str || str === '{}' || str === '[]') return noneNode;

  let parsed: unknown;
  try {
    parsed = JSON.parse(str);
  } catch {
    // Not valid JSON — fall back to showing the raw text.
    return <Typography variant="body2" sx={{ fontSize: 12, wordBreak: 'break-word' }}>{str}</Typography>;
  }

  const lines: string[] = [];
  const pushEntries = (obj: Record<string, unknown>) => {
    Object.entries(obj).forEach(([k, v]) => {
      const val = v !== null && typeof v === 'object' ? JSON.stringify(v) : String(v);
      lines.push(`${k}: ${val}`);
    });
  };

  if (Array.isArray(parsed)) {
    parsed.forEach((item) => {
      if (item !== null && typeof item === 'object') pushEntries(item as Record<string, unknown>);
      else lines.push(String(item));
    });
  } else if (parsed !== null && typeof parsed === 'object') {
    pushEntries(parsed as Record<string, unknown>);
  } else {
    lines.push(String(parsed));
  }

  if (lines.length === 0) return noneNode;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {lines.map((line, index) => (
        <Typography
          key={`${index}-${line}`}
          variant="body2"
          component="div"
          sx={{ fontSize: 12, lineHeight: 1.45, wordBreak: 'break-word' }}
        >
          {line}
        </Typography>
      ))}
    </Box>
  );
};

/**
 * Render a JSON string holding an array of strings (e.g. asset paths) as
 * one item per line. Empty, null, or unparseable input renders nothing.
 */
export const TableJsonArrayMultilineCell: React.FC<{ value: unknown }> = ({ value }) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  if (!str || str === '[]') return null;

  let items: string[] = [];
  try {
    const parsed = JSON.parse(str);
    if (Array.isArray(parsed)) {
      items = parsed
        .map((p) => (p !== null && typeof p === 'object' ? JSON.stringify(p) : String(p)))
        .filter((p) => p.trim() !== '');
    }
  } catch {
    // The value looks like a JSON array but is not valid JSON (e.g. it was
    // truncated mid-string during ingestion, leaving an unterminated last
    // item with no closing "]"). Best-effort: extract every complete
    // double-quoted string so each path still renders on its own line. Any
    // trailing partial item is silently dropped.
    const matches = str.match(/"((?:[^"\\]|\\.)*)"/g);
    if (matches && matches.length > 0) {
      items = matches
        .map((m) => {
          try {
            return JSON.parse(m) as string;
          } catch {
            return m.replace(/^"|"$/g, '');
          }
        })
        .filter((p) => p.trim() !== '');
    } else {
      return <Typography variant="body2" sx={{ fontSize: 12, wordBreak: 'break-all' }}>{str}</Typography>;
    }
  }

  if (items.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
      {items.map((item, index) => (
        <Typography
          key={`${index}-${item}`}
          variant="body2"
          component="div"
          sx={{ fontSize: 12, lineHeight: 1.45, wordBreak: 'break-all' }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  );
};

export const renderFormattedTableCell = (
  raw: unknown,
  displayAs?: TableColumnDisplayAs,
): React.ReactNode => {
  if (displayAs === 'url_actions') {
    return <TableUrlActionsCell url={raw === null || raw === undefined ? '' : String(raw)} />;
  }
  if (displayAs === 'comma_multiline') {
    return <TableCommaMultilineCell value={raw} />;
  }
  if (displayAs === 'json_array_multiline') {
    return <TableJsonArrayMultilineCell value={raw} />;
  }
  if (displayAs === 'bytes_mb') {
    return formatBytesAsMegabytes(raw);
  }
  if (displayAs === 'duration_min') {
    return formatDurationAsMinutes(raw);
  }
  if (displayAs === 'count_capped_100') {
    return formatCountCapped100(raw);
  }
  if (displayAs === 'link_click_here') {
    return <TableLinkClickHereCell url={raw === null || raw === undefined ? '' : String(raw)} />;
  }
  if (displayAs === 'filters_json') {
    return <TableFiltersJsonCell value={raw} />;
  }
  if (displayAs === 'enabled_disabled') {
    const b = toBooleanish(raw);
    if (b === null) return '—';
    return b ? 'Enabled' : 'Disabled';
  }
  return null;
};
